import { useCallback, useId, useRef, useState, type Ref } from 'react';
import { helpLinks, type HelpContext, type HelpLink } from '@shared/help';
import { LEGAL_IDENTITY } from '@shared/legal-identity';
import { Popover } from './Popover';

/**
 * The Help button and its menu (4.1.56): a small circled question mark
 * beside the Appearance control on the homepage, and in the console's
 * top bar on every screen. What it lists comes from `@shared/help`,
 * which is where a destination is connected; this file only draws it.
 */
export function HelpGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.4 9.3a2.7 2.7 0 1 1 3.9 2.4c-.8.4-1.3 1-1.3 1.8v.4" />
      <circle cx="12" cy="17" r=".6" fill="currentColor" />
    </svg>
  );
}

export function HelpButton({ open, onClick, controls, size, ref }: {
  open: boolean;
  onClick: () => void;
  controls?: string;
  size?: 'sm';
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      className={`ui-icon-btn${size === 'sm' ? ' ui-ui-icon-btn--sm' : ''} help-button`}
      aria-label="Help"
      title="Help"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      onClick={onClick}
    >
      <HelpGlyph />
    </button>
  );
}

export type HelpMenuContext = Omit<HelpContext, 'supportEmail'> & { supportEmail?: string | null };

/**
 * The menu. Entries with a destination are links; the rest read "Soon"
 * and are not pressable, so nobody lands on a page that is not there.
 * `onNavigate` lets the console route an in-app path through its router
 * instead of reloading the page.
 */
export function HelpPopover({ open, onClose, anchorRef, context, id, onNavigate }: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  context: HelpMenuContext;
  id?: string;
  onNavigate?: (href: string) => void;
}) {
  const links = helpLinks({ ...context, supportEmail: context.supportEmail === undefined ? LEGAL_IDENTITY.legalSupportEmail : context.supportEmail });
  const anySoon = links.some((l) => !l.href);
  const follow = (e: React.MouseEvent<HTMLAnchorElement>, link: HelpLink) => {
    if (link.external || !onNavigate || !link.href || !link.href.startsWith('/')) { onClose(); return; }
    e.preventDefault();
    onClose();
    onNavigate(link.href);
  };
  return (
    <Popover open={open} onClose={onClose} anchorRef={anchorRef} label="Help" id={id} className="ui-popover--help">
      <p className="ui-popover__title">Help &amp; Support</p>
      <ul className="help-menu">
        {links.map((link) => (
          <li key={link.key} className={link.key === 'topic' ? 'help-menu__item help-menu__item--context' : 'help-menu__item'}>
            {link.href ? (
              <a
                href={link.href}
                onClick={(e) => follow(e, link)}
                {...(link.external && /^https?:/.test(link.href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                <span>{link.label}</span>
                {link.external && <span className="help-menu__ext" aria-hidden="true">↗</span>}
              </a>
            ) : (
              <span className="help-menu__soon" aria-disabled="true">
                <span>{link.label}</span>
                <span className="help-menu__note">Soon</span>
              </span>
            )}
          </li>
        ))}
      </ul>
      {anySoon && <p className="help-menu__hint">Items marked Soon are on their way.</p>}
    </Popover>
  );
}

/** Button and menu together — what a header drops in. */
export function Help({ context, size, onNavigate }: { context: HelpMenuContext; size?: 'sm'; onNavigate?: (href: string) => void }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <HelpButton ref={button} open={open} onClick={() => setOpen((o) => !o)} controls={id} size={size} />
      <HelpPopover open={open} onClose={close} anchorRef={button} context={context} id={id} onNavigate={onNavigate} />
    </>
  );
}
